import saveAs from 'file-saver';
import { calculateAverageMetrics, calculateCompetitorAverageMetrics, extractNumberValue } from './calculators';
import { RealtimeChartDataResponse } from '@/features/realtimeProperty/api/fetchRealtimeProperty';
import { InternalProperty } from '@/features/property/types';
import { getCountryNameFromCode } from '@/utils';
import { TimeseriesDataPoint } from '@/features/charts/types';

/**
 * Interface for daily data records in Excel export
 */
interface DailyDataRecord {
  'Date': string;
  'Property': string;
  'Type': string;
  'Price': number | null;
  'Rank': number | null;
  'Total Properties': number | null;
  'Median Price': number | null;
  'Median Rank': number | null;
}

export const exportFreeData = async (
  chartData: RealtimeChartDataResponse,
  mainProperty: InternalProperty,
  filename?: string
) => {
  // Dynamically import xlsx library to avoid bundling it if not used
  // @ts-ignore
  const XLSX = await import('xlsx');

  // Create workbook
  const workbook = XLSX.utils.book_new();

  // FIRST SHEET: Daily Data Points (if available)
  if (chartData.property && chartData.property.data_points && chartData.property.data_points.length > 0) {
    const dailyData: DailyDataRecord[] = [];

    // Process main property daily data
    chartData.property.data_points.forEach((dataPoint: TimeseriesDataPoint) => {
      dailyData.push({
        'Date': dataPoint.date,
        'Property': chartData.property.name,
        'Type': 'Main Property',
        'Price': dataPoint.price.our,
        'Rank': dataPoint.rank.our,
        'Total Properties': dataPoint.total_properties,
        'Median Price': dataPoint.price.median,
        'Median Rank': dataPoint.rank.median
      });
    });

    // Process competitors daily data
    chartData.competitors.forEach(competitor => {
      // Competitors have different data structure than main property
      if (Array.isArray(competitor.data_points)) {
        competitor.data_points.forEach(dataPoint => {
          dailyData.push({
            'Date': dataPoint.date || '',
            'Property': competitor.name,
            'Type': 'Competitor',
            'Price': extractNumberValue(dataPoint.price),
            'Rank': extractNumberValue(dataPoint.rank),
            'Total Properties': null,
            'Median Price': null,
            'Median Rank': null
          });
        });
      }
    });

    // Sort by date
    dailyData.sort((a, b) => {
      if (a['Date'] < b['Date']) return -1;
      if (a['Date'] > b['Date']) return 1;
      return 0;
    });

    const dailyWorksheet = XLSX.utils.json_to_sheet(dailyData);
    XLSX.utils.book_append_sheet(workbook, dailyWorksheet, 'Daily Data');
  }

  // SECOND SHEET: Property Analysis Summary
  const exportData = [];

  // Process main property
  if (chartData.property) {
    const property = chartData.property;

    // Calculate average metrics from data_points
    const { avgPrice, avgRank } = calculateAverageMetrics(property.data_points);

    exportData.push({
      'Property ID': property.property_id,
      'Property Name': property.name,
      'Country': getCountryNameFromCode(mainProperty.country_code),
      'Type': 'Main Property',
      'Average ADR': avgPrice.toFixed(2),
      'Average Rank': avgRank.toFixed(1),
      'Review Score': property.propertyFields.review_score || 0,
      'Review Count': property.propertyFields.review_count || 0,
      'External Star Rating': property.propertyFields.external_star_rating || 0,
      'Preferred Property': property.propertyFields.is_preferred ? 'Yes' : 'No',
      'Sustainable': property.propertyFields.is_sustainable ? 'Yes' : 'No',
      'Path': property.path || ''
    });
  }

  // Process competitors
  if (chartData.competitors && chartData.competitors.length > 0) {
    chartData.competitors.forEach(competitor => {
      // Calculate average metrics from data_points
      const { avgPrice, avgRank } = calculateCompetitorAverageMetrics(competitor.data_points);

      exportData.push({
        'Property ID': competitor.property_id,
        'Property Name': competitor.name,
        'Country': getCountryNameFromCode(competitor.country_code),
        'Type': 'Competitor',
        'Competitor Score': competitor.competitor_score.toFixed(1),
        'Distance (km)': competitor.distance_km.toFixed(2),
        'Average ADR': avgPrice.toFixed(2),
        'Average Rank': avgRank.toFixed(1),
        'Review Score': competitor.propertyFields.review_score || 0,
        'Review Count': competitor.propertyFields.review_count || 0,
        'Matching Days': competitor.matching_details?.values?.matching_days || 0,
        'Total Available Days': competitor.matching_details?.target?.total_available_days || 0
      });
    });
  }

  const summaryWorksheet = XLSX.utils.json_to_sheet(exportData);
  XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Property Analysis');

  // Generate Excel buffer
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

  // Create Blob and save file
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const outputFilename = filename || `property-analysis-${chartData.property.name.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}`;

  saveAs(blob, `${outputFilename}.xlsx`);
};